import { Product } from "#models/product";
import {
  CompleteQuestionnaireParam,
  QuestionnaireDetails,
  QuestionnaireElements,
  QuestionnaireState,
  QuestionNode
} from "#models/questionnaire";
import {
  completeQuestionnaire,
  getLocation,
  getOutcomePublic,
  getProductByIds,
  getQuestionnaireDetailsById,
  getQuestionnaireDetailsPreview,
  getThemeByQuestionnaireId
} from "#services/questionnaire";
import { EStorageKeys, storageGet, storageSet } from "#services/storage";
import { generateId } from "#utils/generate-id";
import { useMutation, UseMutationResult, useQuery, useQueryClient, UseQueryOptions, UseQueryResult } from 'react-query';
const WHITE_LABEL = 'WHITE_LABEL';
type Payload = { questionnaireId: string; enabled: boolean };

export const useQuestionnaireDetails = (id: string, preview: boolean, options?: UseQueryOptions<QuestionnaireDetails, any, QuestionnaireDetails, string[]>) =>
  useQuery<QuestionnaireDetails, any, QuestionnaireDetails, string[]>(
    [id],
    () => {
      if (preview) {
        return getQuestionnaireDetailsPreview(id);
      }
      return getQuestionnaireDetailsById(id);
    },
    {
      ...options
    }
  ) as UseQueryResult<QuestionnaireDetails, any>;

export const useQuestionnaireState = ({
  questionnaireId,
  enabled
}: Payload): [UseQueryResult<QuestionnaireState, any>, UseMutationResult<any, any, QuestionnaireState, any>] => {
  const clientQuery = useQueryClient();
  return [
    useQuery(
      [questionnaireId + '-state'],
      () =>
        storageGet(questionnaireId + '-state').then((res) => {
          if (res) {
            if (!res.id || !res.questionnaireId) {
              if (!res.id) {
                res.id = generateId();
              }
              if (!res.questionnaireId) {
                res.questionnaireId = questionnaireId;
              }
              storageSet(questionnaireId + '-state', res);
            }
            return res;
          } else {
            return storageSet(questionnaireId + '-state', {
              id: generateId(),
              questionnaireId,
              answerSelectionIds: [],
              questionFieldSubmissions: {},
              fieldAnswer: {}
            });
          }
        }),
      { enabled, cacheTime: 0 }
    ),
    useMutation((state: QuestionnaireState) => storageSet(questionnaireId + '-state', state), {
      onSuccess: () => clientQuery.invalidateQueries([questionnaireId + '-state'])
    })
  ];
};

export const useCurrentQuestionId = ({ questionnaireId, enabled }: { questionnaireId: string; enabled: boolean }) =>
  useQuery([questionnaireId + '-current'], () => storageGet(questionnaireId + '-current'), { enabled, cacheTime: 0 });

export const useSetCurrentQuestionId = (questionnaireId: string) => {
  const clientQuery = useQueryClient();
  return useMutation((id: string) => storageSet(questionnaireId + '-current', id), {
    onSuccess: () => clientQuery.invalidateQueries([questionnaireId + '-current'])
  });
};

export const useQuestionQuestionnaire = ({ questionnaireId, enabled }: { questionnaireId: string; enabled: boolean }) =>
  useQuery([questionnaireId + '-questions'], () => storageGet(questionnaireId + '-questions'), {
    enabled,
    cacheTime: 0
  });

export const useSetQuestionQuestionnaire = (questionnaireId: string) => {
  const clientQuery = useQueryClient();
  return useMutation((questions: QuestionNode[]) => storageSet(questionnaireId + '-questions', questions), {
    onSuccess: () => clientQuery.invalidateQueries([questionnaireId + '-questions'])
  });
};

export const useQuestionnaireDate = ({ questionnaireId, enabled }: Payload) =>
  useQuery([questionnaireId + '-date'], () => storageGet(questionnaireId + '-date'), { enabled, cacheTime: 0 });

export const useSetQuestionnaireDate = (questionnaireId: string) => {
  const clientQuery = useQueryClient();
  return useMutation((date: Date) => storageSet(questionnaireId + '-date', date), {
    onSuccess: () => clientQuery.invalidateQueries([questionnaireId + '-date'])
  });
};

export const useQuestionnaireOutcomeId = ({ questionnaireId, enabled }: Payload) =>
  useQuery([questionnaireId + '-outcomeId'], () => storageGet(questionnaireId + '-outcomeId'), {
    staleTime: 0,
    cacheTime: 0,
    enabled
  });

export const useSetQuestionnaireOutcomeId = (questionnaireId: string) => {
  const clientQuery = useQueryClient();
  return useMutation((outcomeId: string) => storageSet(questionnaireId + '-outcomeId', outcomeId), {
    onSuccess: () => clientQuery.invalidateQueries([questionnaireId + '-outcomeId'])
  });
};

export const useQuestionnaireActiveStep = ({
  questionnaireId,
  enabled
}: Payload): [UseQueryResult<any, any>, UseMutationResult<any, unknown, number, any>] => {
  const clientQuery = useQueryClient();
  return [
    useQuery(
      [questionnaireId + '-activeStep'],
      () =>
        storageGet(questionnaireId + '-activeStep').then((res) => {
          if (res) {
            return res;
          } else {
            return storageSet(questionnaireId + '-activeStep', 1);
          }
        }),
      { enabled, cacheTime: 0 }
    ),
    useMutation((activeStep: number) => storageSet(questionnaireId + '-activeStep', activeStep), {
      onSuccess: () => clientQuery.invalidateQueries([questionnaireId + '-activeStep'])
    })
  ];
};

export const useQuestionnaireTheme = (questionnaireId: string, options?: UseQueryOptions<any, any, any, [string]>) =>
  useQuery<any, any, any, [string]>([`theme-${questionnaireId}`], () => getThemeByQuestionnaireId(questionnaireId), {
    ...options
  }) as UseQueryResult<any, QuestionnaireElements>;

export const useCurrentTheme = (enabled: boolean) =>
  useQuery([EStorageKeys.CURRENT_THEME], () => storageGet(EStorageKeys.CURRENT_THEME), { enabled });

export const useCompleteQuestionnaire = () =>
  useMutation((params: CompleteQuestionnaireParam) => completeQuestionnaire(params));

export const useOutcomePublic = (id: string, getDataOutcome?: (data: any) => void) =>
  useQuery([id], () => getOutcomePublic(id), {
    retry: false,
    onSuccess: (data) => {
      if (getDataOutcome) {
        getDataOutcome(data);
      }
    },
    enabled: !!id
  });

export const useProducts = (
  ids: string[],
  options?: UseQueryOptions<Product[], any, Product[], [string]>
) =>
  useQuery<Product[], any, Product[], [string]>(
    [ids.join(',')],
    () => getProductByIds(ids),
    {
      ...options
    }
  );

export const useCurrencyStorage = () =>
  useQuery(['GET_CURRENCY_STORAGE'], () => {
    return storageGet(EStorageKeys.CURRENCY).then((data) => {
      return { name: data['Currency.Name'], symbol: data['Currency.Symbol'] };
    });
  });

export const useGetLocationById = (id: string) =>
  useQuery([`questionLocation-${id}`], () => getLocation(id), { enabled: Boolean(id) });
